/**
 * Cascade Coil strike timeline — shared by the store (which holds the tower
 * descent while the strike plays) and the renderer (which plays the VFX).
 * Pure numbers, no pixi imports, so the store can depend on it.
 */

export const POP_SPIN_MS = 250;
export const CASCADE_ARC_STAGGER_MS = 50;
export const CASCADE_ARC_GROW_MS = 120;
export const CASCADE_ARC_HOLD_MS = 90;

/** Longest a whole Cascade Coil strike may take to fan out, in ms. */
const CASCADE_ARC_MAX_SPREAD_MS = 800;

/**
 * When cascade arc `sortedIdx` (0-based, sorted nearest-first by
 * `createCascadeArcEffect`) has just finished holding at its target and
 * the gear should start destroying. The pop kicks off at
 * `GROW + HOLD` — not just `GROW` — so the bolt is visibly touching the
 * gear for a beat before it disintegrates, rather than the gear
 * shrinking the instant the tip arrives.
 */
export function cascadeArcArrivalMs(sortedIdx: number, staggerMs: number): number {
  return sortedIdx * staggerMs + CASCADE_ARC_GROW_MS + CASCADE_ARC_HOLD_MS;
}

/**
 * Per-arc stagger for a Cascade Coil strike of `arcCount` arcs. Small
 * strikes keep the 50ms rhythm; a Mk. V clear compresses so the last gear
 * still goes within ~0.6s.
 */
export function cascadeArcStaggerMs(arcCount: number): number {
  if (arcCount <= 1) return CASCADE_ARC_STAGGER_MS;
  return Math.min(CASCADE_ARC_STAGGER_MS, CASCADE_ARC_MAX_SPREAD_MS / (arcCount - 1));
}

/**
 * Total time from a Cascade Coil strike firing until its last struck gear
 * has finished disintegrating (and any held floaters are released to fall).
 * The store holds the tower descent for this long so the held stand-in
 * gears, which sit at fixed screen positions, never drift off their cells.
 */
export function cascadeStrikeDurationMs(arcCount: number): number {
  if (arcCount <= 0) return 0;
  return cascadeArcArrivalMs(arcCount - 1, cascadeArcStaggerMs(arcCount)) + POP_SPIN_MS;
}
